import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';


import { HomeMainMenuComponent } from '../../shared/home-main-menu/home-main-menu.component'; 

import { ScriptLoaderService, VENDOR_SCRIPTS } from '../../script-loader.service';
import { SubscribeComponent } from '../../shared/subscribe/subscribe.component';

declare let jQuery: any;

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, HomeMainMenuComponent, RouterLink, SubscribeComponent],
  templateUrl: '../templates/home.component.html',
})
export class HomeComponent implements AfterViewInit, OnDestroy {
  constructor(private readonly scriptLoader: ScriptLoaderService) {}

  ngAfterViewInit(): void {
    document.body.classList.add('defult-home');
    this.scriptLoader
      .loadSequential(...VENDOR_SCRIPTS)
      .then(() => {
        // Use setTimeout to ensure DOM is fully rendered
        setTimeout(() => {
          // Reinitialize sticky menu
          this.initializeStickyMenu();
          
          // Reinitialize Owl Carousel for testimonial section
          this.initializeOwlCarousel();
          
          // Reinitialize js-tilt
          this.initializeTilt();
          
          // If vendor scripts were injected after the native load event,
          // re-dispatch it so jQuery load handlers in main.js still run.
          if (document.readyState === 'complete') {
            window.dispatchEvent(new Event('load'));
          }

          this.initializeYoutubeLink();
        }, 0);
      })
      .catch((error) => {
        console.error('Failed to load third-party scripts', error);
      });
  }

  private initializeStickyMenu(): void {
    const $ = jQuery;
    if (!$) {
      console.warn('jQuery not loaded');
      return;
    }

    const header = $('.menu-sticky');
    if (!header.length) {
      return;
    }

    // Remove any existing scroll handlers on window to avoid duplicates
    $(window).off('scroll.sticky-menu');

    // Reattach scroll event handler for sticky menu
    $(window).on('scroll.sticky-menu', () => {
      const scroll = $(window).scrollTop() || 0;
      if (scroll < 1) {
        header.removeClass('sticky');
      } else {
        header.addClass('sticky');
      }

      // Also handle section loaded class
      $('section').each((index: number, element: Element) => {
        const $element = $(element);
        const elementTop = ($element.offset()?.top || 0) - ($('#rs-header').outerHeight() || 0);
        if (scroll >= elementTop) {
          $element.addClass('loaded');
        }
      });
    });

    // Trigger scroll event once to set initial state
    $(window).trigger('scroll.sticky-menu');
  }

  private initializeOwlCarousel(): void {
    const $ = jQuery;
    if (!$ || !$.fn.owlCarousel) {
      console.warn('Owl Carousel not loaded');
      return;
    }

    // Destroy existing carousels first to avoid duplicate initialization
    $('.rs-carousel').each((index: number, element: Element) => {
      const $element = $(element);
      if ($element.hasClass('owl-carousel')) {
        $element.trigger('destroy.owl.carousel');
      }
    });

    // Initialize all Owl Carousels with their data attributes
    $('.rs-carousel').each((index: number, element: Element) => {
      const owlCarousel = $(element);
      const loop = owlCarousel.data('loop');
      const items = owlCarousel.data('items');
      const margin = owlCarousel.data('margin');
      const stagePadding = owlCarousel.data('stage-padding');
      const autoplay = owlCarousel.data('autoplay');
      const autoplayTimeout = owlCarousel.data('autoplay-timeout');
      const smartSpeed = owlCarousel.data('smart-speed');
      const dots = owlCarousel.data('dots');
      const nav = owlCarousel.data('nav');
      const navSpeed = owlCarousel.data('nav-speed');
      const xsDevice = owlCarousel.data('mobile-device');
      const xsDeviceNav = owlCarousel.data('mobile-device-nav');
      const xsDeviceDots = owlCarousel.data('mobile-device-dots');
      const smDevice = owlCarousel.data('ipad-device');
      const smDeviceNav = owlCarousel.data('ipad-device-nav');
      const smDeviceDots = owlCarousel.data('ipad-device-dots');
      const smDevice2 = owlCarousel.data('ipad-device2');
      const smDeviceNav2 = owlCarousel.data('ipad-device-nav2');
      const smDeviceDots2 = owlCarousel.data('ipad-device-dots2');
      const mdDevice = owlCarousel.data('md-device');
      const mdDeviceNav = owlCarousel.data('md-device-nav');
      const mdDeviceDots = owlCarousel.data('md-device-dots');
      const centerMode = owlCarousel.data('center-mode');
      const HoverPause = owlCarousel.data('hoverpause');

      owlCarousel.owlCarousel({
        loop: (loop ? true : false),
        items: (items ? items : 1),
        margin: (margin ? margin : 0),
        stagePadding: (stagePadding ? stagePadding : 0),
        autoplay: (autoplay ? true : false),
        autoplayTimeout: (autoplayTimeout ? autoplayTimeout : 1000),
        smartSpeed: (smartSpeed ? smartSpeed : 250),
        dots: (dots ? true : false),
        nav: (nav ? true : false),
        navSpeed: (navSpeed ? navSpeed : false),
        center: (centerMode ? true : false),
        mouseDrag: true,
        touchDrag: true,
        pullDrag: true,
        freeDrag: false,
        autoplayHoverPause: (HoverPause ? true : false),
        responsive: {
          0: {
            items: (xsDevice ? xsDevice : 1),
            nav: (xsDeviceNav ? true : false),
            dots: (xsDeviceDots ? true : false),
          },
          768: {
            items: (smDevice ? smDevice : 1),
            nav: (smDeviceNav ? true : false),
            dots: (smDeviceDots ? true : false),
          },
          1024: {
            items: (smDevice2 ? smDevice2 : 1),
            nav: (smDeviceNav2 ? true : false),
            dots: (smDeviceDots2 ? true : false),
          },
          1200: {
            items: (mdDevice ? mdDevice : 1),
            nav: (mdDeviceNav ? true : false),
            dots: (mdDeviceDots ? true : false),
          }
        }
      });
    });
  }

  private initializeTilt(): void {
    const $ = jQuery;
    if (!$ || !$.fn.tilt) {
      console.warn('Tilt plugin not loaded');
      return;
    }

    const tilt = $('.js-tilt');
    if (tilt.length) {
      $('.js-tilt').tilt();
    }
  }

  private initializeYoutubeLink(): void {
    const $ = jQuery;
    if (!$) {
      return;
    }

    const youtubeLink = $('.rs-video-home9 .popup-videos');
    if (!youtubeLink.length) {
      return;
    }

    youtubeLink.off('click.magnificPopup');
  }

  ngOnDestroy(): void {
    document.body.classList.remove('defult-home');
  }

}

